// OWNER: FRONTEND-ADMIN. Settings → State rules (SPEC §5.1, §15.9). Opening a US state (calling or drops) or loosening
// one of its conditions (all-lines DNC scrub, business lines only, setter note; migration 0005; hand-pick only, 0006)
// needs a note saying what was checked and at least one source URL, and saves the state as reviewed. Texas/Quebec calling
// and Canadian drops stay off. PJ 2026-10-03: drop_allowed no longer gates US drops (every state but CA/WA/TX takes them).
import { useMemo, useState } from 'preact/hooks';
import type { CallingWindow, StateRule } from '../../../src/shared/api';
import type { SettingsView } from '../../../src/shared/api-core';
import { errorMessage } from '../api';
import { ErrorBanner, Modal, fmtDateTime, toast } from '../components/ui';
import { adminApi, type AdminStateRulePatch } from './endpoints';
import { Check, Field, ProblemList, inputValue, useLoad } from './ui';
import {
  LOCKED_OFF, MAX_SETTER_NOTE, STATE_CONDITION_HELP, adminBoundsText, isHttpUrl, normalizeSetterNote, setterNoteProblems,
  stateConditionChips, stateRuleLoosening, stateRuleProblems, unsavedStateFields,
} from './validate';

type Filter = 'all' | 'calling' | 'closed' | 'unreviewed' | 'drops' | 'conditions';

const fmtWindow = (w: CallingWindow | null): string => {
  if (!w) return '';
  const part = (p: { start: string; end: string } | null) => (p ? `${p.start}–${p.end}` : 'closed');
  return `Mon–Fri ${part(w.weekday)} · Sat ${part(w.saturday)} · Sun ${part(w.sunday ?? null)}`;
};

/** True when this server sends migration 0005's fields (an older one leaves them out). */
const hasConditions = (r: StateRule): boolean => typeof (r as Partial<StateRule>).scrubAllLines === 'boolean';
/** True when this server sends migration 0006's hand-pick-only flag. */
const hasHandPick = (r: StateRule): boolean => typeof (r as Partial<StateRule>).handPickOnly === 'boolean';

export function StateRulesAdmin(props: { settings: SettingsView | null }) {
  const rules = useLoad(() => adminApi.stateRules(), []);
  const [country, setCountry] = useState<'' | 'US' | 'CA'>('');
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<StateRule | null>(null);

  const withConditions = (r: StateRule) => stateConditionChips(r).length > 0;
  const rows = useMemo(() => (rules.data ?? []).filter((r) => {
    if (country && r.country !== country) return false;
    if (q && !`${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === 'calling') return r.callingAllowed;
    if (filter === 'closed') return !r.callingAllowed;
    if (filter === 'unreviewed') return r.country === 'US' && !r.reviewedAt;
    if (filter === 'drops') return r.dropAllowed;
    if (filter === 'conditions') return withConditions(r);
    return true;
  }).sort((a, b) => a.country.localeCompare(b.country) || a.name.localeCompare(b.name)), [rules.data, country, filter, q]);

  const all = rules.data ?? [];
  const count = (f: (r: StateRule) => boolean) => all.filter(f).length;

  return (
    <div class="stack">
      <div class="adm-sub">
        Where setters may call. A US state is dialled only when calling is on <b>and</b> it has been reviewed (registration, state do-not-call list,
        calling hours). Some open states add conditions: a Do Not Call scrub on every line, business landlines only, a line the setter must say,
        or hand-pick only (the dialer never picks those leads by itself; setters open each one from Leads).
        Voicemail drops no longer depend on this table: they work on verified US business landlines in every state except California, Washington
        and Texas, and never into Canada (the Drops column is kept for the record).
        Default hours: US Mon–Fri {fmtWindow(props.settings?.callingWindows.US ?? null).replace(/^Mon–Fri /, '')} · CA Mon–Fri {fmtWindow(props.settings?.callingWindows.CA ?? null).replace(/^Mon–Fri /, '')} (prospect-local).
      </div>
      <div class="adm-toolbar">
        <select value={country} onChange={(e) => setCountry(inputValue(e) as '' | 'US' | 'CA')}><option value="">US + Canada</option><option value="US">US</option><option value="CA">Canada</option></select>
        {([['all', `All (${all.length})`], ['calling', `Calling on (${count((r) => r.callingAllowed)})`], ['closed', `Closed (${count((r) => !r.callingAllowed)})`],
          ['unreviewed', `US unreviewed (${count((r) => r.country === 'US' && !r.reviewedAt)})`], ['drops', `Drops on (${count((r) => r.dropAllowed)})`],
          ['conditions', `With conditions (${count(withConditions)})`]] as Array<[Filter, string]>).map(([k, label]) => (
          <button class={`btn btn-sm ${filter === k ? 'btn-ink' : ''}`} onClick={() => setFilter(k)}>{label}</button>
        ))}
        <span class="grow" />
        <input placeholder="Search state" value={q} onInput={(e) => setQ(inputValue(e))} />
      </div>
      <ErrorBanner error={rules.error} />
      {rows.length ? (
        <div class="adm-table-wrap" style="max-height:65vh">
          <table>
            <thead><tr><th>State</th><th>Calling</th><th>Drops</th><th>Conditions</th><th>Hours override</th><th>Reviewed</th><th>Notes</th><th>Sources</th><th /></tr></thead>
            <tbody>{rows.map((r) => {
              const dialable = r.callingAllowed && (r.country === 'CA' || !!r.reviewedAt);
              const chips = stateConditionChips(r);
              return (
                <tr>
                  <td><b>{r.name}</b> <span class="mono muted">{r.code}</span></td>
                  <td>{dialable ? <span class="chip chip-ok">on</span> : r.callingAllowed ? <span class="chip chip-warn" title="Calling is on but the state isn't reviewed, so the gate still blocks it">on · unreviewed</span> : <span class="chip chip-bad">off</span>}</td>
                  <td>{r.dropAllowed ? <span class="chip chip-ok">on</span> : <span class="chip">off</span>}</td>
                  <td style="min-width:130px">{chips.length
                    ? <div class="adm-chips">{chips.map((c) => <span class={`chip ${c.tone === 'warn' ? 'chip-warn' : 'chip-info'}`} title={c.title}>{c.text}</span>)}</div>
                    : <span class="muted">—</span>}</td>
                  <td style="font-size:13px">{fmtWindow(r.window) || <span class="muted">default</span>}</td>
                  <td>{r.reviewedAt ? fmtDateTime(r.reviewedAt) : r.country === 'US' ? <span class="chip chip-warn">not reviewed</span> : <span class="muted">—</span>}</td>
                  <td style="max-width:360px;font-size:13px">{r.notes ? (r.notes.length > 140 ? r.notes.slice(0, 140) + '…' : r.notes) : <span class="muted">—</span>}</td>
                  <td>{r.sources.length ? r.sources.map((s, i) => <a href={s} target="_blank" rel="noopener noreferrer" style="margin-right:6px">[{i + 1}]</a>) : <span class="muted">none</span>}</td>
                  <td><button class="btn btn-sm" onClick={() => setEditing(r)}>Edit</button></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      ) : rules.data ? <div class="muted">No states match.</div> : null}
      {editing ? <EditStateRule rule={editing} onClose={() => setEditing(null)} onSaved={(r) => rules.setData((rules.data ?? []).map((x) => (x.code === r.code ? r : x)))} /> : null}
    </div>
  );
}

function EditStateRule(props: { rule: StateRule; onClose: () => void; onSaved: (r: StateRule) => void }) {
  const r = props.rule;
  const supported = hasConditions(r);
  const [callingAllowed, setCalling] = useState(r.callingAllowed);
  const [dropAllowed, setDrops] = useState(r.dropAllowed);
  const [scrubAllLines, setScrub] = useState(supported ? r.scrubAllLines : false);
  const [businessLinesOnly, setBizOnly] = useState(supported ? r.businessLinesOnly : false);
  const [setterNote, setSetterNote] = useState(supported ? r.setterNote ?? '' : '');
  const handPickSupported = hasHandPick(r);
  const [handPickOnly, setHandPick] = useState(handPickSupported ? r.handPickOnly : false);
  const [override, setOverride] = useState(!!r.window);
  const [weekday, setWeekday] = useState(r.window?.weekday ?? { start: '09:00', end: '20:00' });
  const [satOpen, setSatOpen] = useState(r.window ? !!r.window.saturday : true);
  const [saturday, setSaturday] = useState(r.window?.saturday ?? { start: '10:00', end: '17:00' });
  const [weekdayOpen, setWeekdayOpen] = useState(r.window ? !!r.window.weekday : true);
  const [registrationNote, setRegistrationNote] = useState(r.registrationNote ?? '');
  const [notes, setNotes] = useState(r.notes ?? '');
  const [sourcesText, setSourcesText] = useState(r.sources.join('\n'));
  const [markReviewed, setMarkReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sources = sourcesText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  // A Sunday block set through the API is kept as is (this form has no Sunday row: a state's own hours mean no Sunday calls).
  const hours: CallingWindow | null = override
    ? { weekday: weekdayOpen ? weekday : null, saturday: satOpen ? saturday : null, ...(r.window?.sunday ? { sunday: r.window.sunday } : {}) }
    : null;
  const draft = {
    callingAllowed, dropAllowed, window: hours, registrationNote: registrationNote || null, notes: notes || null, sources,
    ...(supported ? { scrubAllLines, businessLinesOnly, setterNote: normalizeSetterNote(setterNote) } : {}),
    ...(handPickSupported ? { handPickOnly } : {}),
  };
  const loosening = stateRuleLoosening(r, draft);
  const needsReview = r.country === 'US' && loosening.length > 0;
  const review = markReviewed || needsReview;
  const problems = stateRuleProblems(r, draft, review);
  const noteProblems = supported ? setterNoteProblems(setterNote) : [];
  const locked = LOCKED_OFF[r.code];
  const usOnly = r.country === 'CA';

  const save = async () => {
    if (problems.length) { setError(problems[0]); return; }
    setBusy(true); setError(null);
    // conditions go only when changed, so the check below can tell whether the server stored them
    const cond: Pick<AdminStateRulePatch, 'scrubAllLines' | 'businessLinesOnly' | 'setterNote' | 'handPickOnly'> = {};
    if (supported) {
      if (scrubAllLines !== r.scrubAllLines) cond.scrubAllLines = scrubAllLines;
      if (businessLinesOnly !== r.businessLinesOnly) cond.businessLinesOnly = businessLinesOnly;
      if (normalizeSetterNote(setterNote) !== normalizeSetterNote(r.setterNote)) cond.setterNote = normalizeSetterNote(setterNote);
    }
    if (handPickSupported && handPickOnly !== r.handPickOnly) cond.handPickOnly = handPickOnly;
    try {
      const next = await adminApi.patchStateRule(r.code, {
        callingAllowed, dropAllowed, window: hours, registrationNote: registrationNote.trim() || null, notes: notes.trim() || null, sources,
        ...cond,
        ...(review ? { markReviewed: true } : {}),
      });
      props.onSaved(next);
      const missing = unsavedStateFields(cond, next);
      if (missing.length) {
        // fail loudly: the rest saved, these didn't
        setError(`Saved, but the server didn't store ${missing.join(', ')}. Its state-rules update doesn't take the conditions yet (CORE: migrations 0005/0006 and PATCH /api/settings/state-rules).`);
        toast(`${r.name}: some changes weren't stored.`, 'warn');
        return;
      }
      toast(`${r.name} saved.`, 'ok');
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`${r.name} (${r.code})`} onClose={props.onClose} width={760}>
      <div class="stack">
        <div class="row" style="gap:18px;flex-wrap:wrap">
          <Check checked={callingAllowed} disabled={!!locked && !r.callingAllowed} onChange={setCalling} label={<b>Calling allowed</b>} title={locked} />
          <Check checked={dropAllowed} disabled={r.country === 'CA'} onChange={setDrops} label={<b>Voicemail drops allowed</b>} title={r.country === 'CA' ? 'Never for Canadian numbers' : undefined} />
        </div>
        {locked ? <div class="chip chip-bad" style="display:block;padding:6px 10px">{locked}</div> : null}
        {r.country === 'CA' ? <div class="adm-sub">Canada never gets voicemail drops (CRTC ADAD rules).</div> : null}

        <div class="card stack" style="background:var(--paper);gap:8px">
          <b>Conditions for calls here</b>
          {supported ? (
            <>
              <div class="stack" style="gap:2px">
                <Check checked={scrubAllLines} disabled={usOnly && !r.scrubAllLines} onChange={setScrub} title={usOnly ? 'The national Do Not Call scrub is for US numbers' : undefined}
                  label={<b>Scrub every line against the Do Not Call list</b>} />
                <div class="adm-sub" style="margin-left:22px">{STATE_CONDITION_HELP.scrubAllLines}</div>
              </div>
              <div class="stack" style="gap:2px">
                <Check checked={businessLinesOnly} disabled={usOnly && !r.businessLinesOnly} onChange={setBizOnly} title={usOnly ? 'Line-type lookups run on US numbers only' : undefined}
                  label={<b>Business lines only</b>} />
                <div class="adm-sub" style="margin-left:22px">{STATE_CONDITION_HELP.businessLinesOnly}</div>
              </div>
              {handPickSupported ? (
                <div class="stack" style="gap:2px">
                  <Check checked={handPickOnly} onChange={setHandPick} label={<b>Hand-pick only</b>} />
                  <div class="adm-sub" style="margin-left:22px">{STATE_CONDITION_HELP.handPickOnly}</div>
                </div>
              ) : null}
              <Field label="Setter note" hint={<span>{STATE_CONDITION_HELP.setterNote} {normalizeSetterNote(setterNote)?.length ?? 0}/{MAX_SETTER_NOTE}</span>}>
                <textarea rows={2} value={setterNote} maxLength={MAX_SETTER_NOTE + 100} placeholder="e.g. Tip: after your intro, ask if it's OK to continue." onInput={(e) => setSetterNote(inputValue(e))} />
              </Field>
              <ProblemList problems={noteProblems.filter((p) => p.level === 'warn')} />
            </>
          ) : (
            <div class="adm-muted-box">This server doesn't send the state conditions yet (CORE adds migration 0005: all-lines DNC scrub, business lines only, setter note).</div>
          )}
        </div>

        {needsReview ? (
          <div class="adm-muted-box">
            You're {loosening.join(', ')}. Write what you checked in the notes
            ({dropAllowed && !r.dropAllowed ? 'the state’s prerecorded-message / ADAD statute and whether it has a business exemption' : 'telemarketer registration, the state do-not-call list, calling-hour and line-type rules'})
            and add the source link(s). Saving marks the state reviewed today.
          </div>
        ) : null}

        <Field label="Notes (what was checked, and the conclusion)"><textarea rows={4} value={notes} onInput={(e) => setNotes(inputValue(e))} /></Field>
        <Field label="Sources (one URL per line)" hint={sources.filter((s) => !isHttpUrl(s)).length ? <span class="chip chip-bad">Some lines aren't web links</span> : `${sources.length} source${sources.length === 1 ? '' : 's'}`}>
          <textarea rows={3} value={sourcesText} placeholder="https://…" onInput={(e) => setSourcesText(inputValue(e))} />
        </Field>
        <Field label="Registration note (optional)" hint="e.g. registration number and renewal date, if the state requires one"><input value={registrationNote} onInput={(e) => setRegistrationNote(inputValue(e))} /></Field>

        <Check checked={override} onChange={setOverride} label="Stricter calling hours than the default" />
        {override ? (
          <>
            <div class="adm-sub">Prospect-local. {r.country === 'US' ? 'US' : 'Canadian'} windows must sit inside {adminBoundsText(r.country)} (the legal hours less a margin for time-zone guesses). With its own hours, this state gets no Sunday calls unless they include Sunday hours (set through the API).</div>
            <div class="adm-grid">
              <div class="stack">
                <Check checked={weekdayOpen} onChange={setWeekdayOpen} label="Mon–Fri open" />
                {weekdayOpen ? <div class="row"><input type="time" value={weekday.start} onInput={(e) => setWeekday({ ...weekday, start: inputValue(e) })} /> – <input type="time" value={weekday.end} onInput={(e) => setWeekday({ ...weekday, end: inputValue(e) })} /></div> : null}
              </div>
              <div class="stack">
                <Check checked={satOpen} onChange={setSatOpen} label="Saturday open" />
                {satOpen ? <div class="row"><input type="time" value={saturday.start} onInput={(e) => setSaturday({ ...saturday, start: inputValue(e) })} /> – <input type="time" value={saturday.end} onInput={(e) => setSaturday({ ...saturday, end: inputValue(e) })} /></div> : null}
              </div>
            </div>
          </>
        ) : null}

        {r.country === 'US' && !needsReview ? (
          <Check checked={markReviewed} onChange={setMarkReviewed} label={r.reviewedAt ? `Re-mark as reviewed today (last reviewed ${fmtDateTime(r.reviewedAt)})` : 'Mark as reviewed today'} />
        ) : null}

        {problems.length ? <ul class="adm-problems">{problems.map((p) => <li class="error">{p}</li>)}</ul> : null}
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>{error && !busy ? 'Close' : 'Cancel'}</button>
          <button class="btn btn-primary" disabled={busy || problems.length > 0} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}
